import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

// Generic collapsible card used for "Pass details" / "What you'll get" /
// "How it works". `onFirstExpand` fires once per mount so section-expand
// telemetry doesn't spam on every toggle. `defaultOpen` lets the desktop
// layout show sections expanded while mobile starts collapsed.
const Collapsible: React.FC<{
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  onFirstExpand?: () => void;
}> = ({ title, children, defaultOpen = false, onFirstExpand }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [expandFired, setExpandFired] = useState(defaultOpen);

  const toggle = () => {
    setOpen((v) => {
      if (!v && !expandFired) {
        setExpandFired(true);
        onFirstExpand?.();
      }
      return !v;
    });
  };

  return (
    <div className="bg-white rounded-2xl border border-[#E4EAE2] overflow-hidden">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left"
      >
        <span className="text-base font-semibold text-gray-900">{title}</span>
        <motion.span
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="text-gray-400 shrink-0"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M6 9l6 6 6-6" />
          </svg>
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Collapsible;
